import { FunctionComponent } from "react";

import { Tooltip } from "@/components/Tooltip/Tooltip";

import { Sender } from "./Sender";

const MAX_SHOWN = 3;

const fullName = (sender: Sender) => `${sender.firstName ?? ""} ${sender.lastName ?? ""}`.trim();

const initials = (sender: Sender) =>
	`${sender.firstName?.[0] ?? ""}${sender.lastName?.[0] ?? ""}`.toUpperCase();

/** Overlapping initials avatars for a campaign's active senders (mockup parity). */
export const SenderAvatars: FunctionComponent<{ senders?: Sender[] }> = ({ senders }) => {
	const active = (senders ?? []).reduce<Sender[]>((acc, sender) => {
		if (sender.active && !acc.some((s) => fullName(s) === fullName(sender))) {
			acc.push(sender);
		}

		return acc;
	}, []);

	if (active.length === 0) {
		return <span className="text-text-meta">—</span>;
	}

	const shown = active.slice(0, MAX_SHOWN);
	const extra = active.length - shown.length;

	return (
		// -space-x-2 overlaps the direct flex children (each avatar's Tooltip wrapper); the
		// white ring keeps them visually separated. Negative margin can't live on the inner
		// span because each sits alone inside its own Tooltip wrapper.
		<div className="flex items-center -space-x-2">
			{shown.map((sender, index) => (
				<Tooltip key={`${fullName(sender)}-${index}`} content={fullName(sender)} variant="dark">
					<span
						className="flex h-7 w-7 items-center justify-center rounded-full border-2 border-white bg-brand-light text-[11px] font-semibold text-white"
						tabIndex={0}
					>
						{initials(sender)}
					</span>
				</Tooltip>
			))}
			{extra > 0 ? (
				<span className="flex h-7 w-7 items-center justify-center rounded-full border-2 border-white bg-gray-100 text-[11px] font-semibold text-text-meta">
					+{extra}
				</span>
			) : null}
		</div>
	);
};
